<template>
    <IonPage>
        <IonContent fullscreen scroll-events @ionScroll="contentScrollY = $event.detail.scrollTop">
            <!-- Add the header over the content in the fixed slot -->
            <AppHeader slot="fixed" ref="headerMenu" :headerImage="headerImage" :has-scrolled="contentScrollY > 2" :class="headerHeightClass">
                <template v-if="moduleTitle" v-slot:title>{{ moduleTitle }}</template>
            </AppHeader>

            <!-- Position the refresher under the app header in the fixed slot -->
            <IonRefresher v-if="(appModule.link && appModule.link.url) || appModule.data?.meta?.refresh_url" slot="fixed" @ionRefresh="doRefresh" :class="headerHeightMarginTopClass">
                <IonRefresherContent />
            </IonRefresher>

            <!-- Spacer to push content after the header -->
            <div
                class="inline"
                :class="{
                    'flex flex-col h-full': fixedHeight,
                }"
            >
                <div v-if="!headerImage" :class="headerHeightClass" />

                <div v-if="loadingId === appModuleId" class="py-10 flex items-center justify-center absolute top-0 left-0 right-0 bottom-0" data-cy="module-loading">
                    <UILoading class="w-12 h-12" />
                </div>

                <div v-else-if="error" class="p-4">
                    <DAlert type="danger" title="Error">
                        Please try again later
                    </DAlert>
                </div>

                <component
                    v-else
                    :is="component"
                    :config="data"
                    class="ion-content-scroll-host"
                    :data-cy="`module-component-${data?.component}`"
                    :class="{
                        'h-full': data && data.items && data.items.length === 0,
                        'flex-1 overflow-hidden': useVirtualScroll,
                    }"
                    :header-menu="headerMenu?.$el"
                />
            </div>
        </IonContent>
        <div v-if="showLoadingOverlay" class="bg-white/50 fixed z-50 inset-0 flex items-center justify-center">
            <UILoading class="w-12 h-12" />
        </div>
    </IonPage>
</template>

<script setup>
    import { DAlert } from '@digistorm/spark'
    import { useRoute, useRouter } from 'vue-router'
    import { omit } from 'lodash'

    import { statusBar } from '@/services'
    import authConstants from '@/constants/auth'

    const route = useRoute()
    const router = useRouter()

    const appStore = useAppStore()
    const authStore = useAuthStore()
    const moduleStore = useModuleStore()
    const deviceStore = useDeviceStore()

    const authHelpers = useAuthHelpers()
    const moduleHelpers = useModuleHelpers()

    const props = defineProps({
        moduleId: String,
    })

    const { isAndroid, isIos } = storeToRefs(deviceStore)
    const { modules, loadingId, fetching } = storeToRefs(moduleStore)

    const component = ref()

    const routeModuleId = ref(route.params.id)
    const error = ref(null)
    const headerMenu = ref(null)
    const contentScrollY = ref(0)
    const showLoadingOverlay = ref(false)
    const headerHeightClass = ref('h-[calc(var(--beyond-safe-area-inset-top)_+_56px)]')
    const headerHeightMarginTopClass = ref('mt-[calc(var(--beyond-safe-area-inset-top)_+_56px)]')

    const appModuleId = computed(() => props.moduleId ?? routeModuleId.value)
    const appModule = computed(() => modules.value[appModuleId.value])
    const data = computed(() => appModule.value.data)

    const moduleTitle = computed(() => {
        return get(data.value, 'meta.title')
    })

    const headerImage = computed(() => {
        return !!get(data.value, 'meta.header_image')
    })

    const useVirtualScroll = computed(() => {
        return get(data.value, 'meta.virtual_scroll')
    })

    const fixedHeight = computed(() => {
        return useVirtualScroll.value || ['CustomMap', 'CenterLayout'].includes(get(appModule.value, 'data.component'))
    })

    const doRefresh = (e) => {
        moduleStore.loadModule(null, true)
            .then(() => {
                e.target.complete()
            })
            .catch(() => {
                e.target.complete()
            })
    }

    const authError = (e) => {
        error.value = e
    }

    const goBack = () => {
        router.back()
    }

    const authAbort = () => {
        goBack()
    }

    const loadModule = async () => {
        if (data.value) {
            component.value = data.value.component
            return
        }
        return moduleStore.loadModule()
            .then(() => {
                // Don't overwrite component if it already exists
                if (isNil(component.value)) {
                    component.value = data.value.component
                }
            })
            .catch((e) => {
                if (authHelpers.isAuthenticationException(e)) {
                    authHelpers.handleAuthenticationException(
                        e.response.data,
                        router,
                        (payload) => {
                            const switchedUsers = payload?.userSwitched ?? true

                            if (switchedUsers) {
                                // Go back to the first level module and refresh
                                const moduleId = first(moduleStore.stack)
                                const openModuleCount = moduleStore.stack.length
                                moduleStore.stack = [moduleId]
                                moduleStore.modules = {
                                    [moduleId]: omit(moduleStore.modules[moduleId], 'data'),
                                }
                                moduleHelpers.navigateBackToFirstModule(openModuleCount)
                            } else {
                                // Reload the same view
                                const moduleId = last(moduleStore.stack)
                                router.replace({ name: 'view', params: { id: moduleId } })
                            }
                        },
                        () => {
                            router.back()
                        },
                    )

                    // Prevent error from appearing while the login view launches
                    return
                }
                if (moduleHelpers.isActionException(e)) {
                    moduleHelpers.handleActionException(
                        e.response.data,
                    )
                }
                error.value = e
            })
    }

    watch(data, (value, oldValue) => {
        // Update component if data changes
        if (get(value, 'component') && value.component !== oldValue?.component) {
            component.value = value.component
        }
    }, { deep: true })

    const onToggleLoadingOverlay = (status) => {
        showLoadingOverlay.value = status
    }

    onMounted(() => {
        console.log('mounted module')
        authStore.events.on('error', authError)
        authStore.events.on('abort', authAbort)
        events.on('toggleLoadingOverlay', onToggleLoadingOverlay)

        loadModule()
    })

    onUnmounted(() => {
        authStore.events.off('error', authError)
        authStore.events.off('abort', authAbort)
        events.off('toggleLoadingOverlay', onToggleLoadingOverlay)
    })

    onIonViewWillEnter(() => {
        statusBar.setTheme()
    })
</script>
